import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ApiPathEnum,
  SubscriptionPaymentEndpointEnum,
} from '../constants/endpoints.enum';
import {
  ApiDataResponse,
  ApiResponse,
} from '../interfaces/api-response.inteface';
import {
  BankAccountFilters,
  BankAccounts,
  BankAccountsResponse,
  ExpensesPerAccount,
  LatestExpenseMovements,
  ProfitPerAgent,
} from '../interfaces/dashboard.interface';
import { DateRange, PaginationParams } from '../interfaces/filters.interface';
import {
  RepeatedVoucherByBankDateRange,
  SubscriptionPayment,
  SubscriptionPaymentListItem,
  SubscriptionPaymentListResponseData,
  SubscriptionPaymentQueryFilters,
  SubscriptionPaymentUpdatePayload,
} from '../interfaces/subscription-payment.interface';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionPaymentService {
  private API_URL = `${environment.API_URL}${ApiPathEnum.SubscriptionPayment}`;
  private readonly defaultLatestExpenseFilters: Record<string, string | number> = {
    limit: 10,
    page: 1,
  };

  constructor(private readonly _httpClient: HttpClient) {}

  private mapApiResponseToSubscriptionPayment(
    res: ApiResponse<SubscriptionPayment[]>
  ): ApiResponse<SubscriptionPayment[]> {
    return {
      ...res,
      data: res.data.map((item) => ({
        ...item,
        monto:
          typeof item.monto === 'string' ? parseFloat(item.monto) : item.monto,
        parseMonto: Math.abs(item.monto),
      })),
    };
  }

  public getFinancialMovementSummary(
    dateRange: DateRange
  ): Observable<ApiResponse<SubscriptionPayment[]>> {
    const endpoint = `${SubscriptionPaymentEndpointEnum.GetFinancialMovementSummary}`;
    const url = `${this.API_URL}${endpoint}`;
    return this._httpClient
      .post<ApiResponse<SubscriptionPayment[]>>(url, { ...dateRange })
      .pipe(map(this.mapApiResponseToSubscriptionPayment));
  }

  public getIncomeAndExpenses(
    dateRange: DateRange
  ): Observable<ApiResponse<SubscriptionPayment[]>> {
    const endpoint = `${SubscriptionPaymentEndpointEnum.GetIncomeAndExpenses}`;
    const url = `${this.API_URL}${endpoint}`;
    return this._httpClient
      .post<ApiResponse<SubscriptionPayment[]>>(url, { ...dateRange })
      .pipe(map(this.mapApiResponseToSubscriptionPayment));
  }

  public getBankAccounts(
    filters: BankAccountFilters
  ): Observable<ApiResponse<BankAccountsResponse>> {
    const endpoint = `${SubscriptionPaymentEndpointEnum.GetBankAccounts}`;
    const url = `${this.API_URL}${endpoint}`;
    return this._httpClient.post<ApiResponse<BankAccountsResponse>>(
      url,
      filters
    );
  }

  public getRepeatedVouchersByBankDateRange(
    dateRange: DateRange
  ): Observable<ApiResponse<RepeatedVoucherByBankDateRange[]>> {
    const endpoint = `${SubscriptionPaymentEndpointEnum.GetRepeatedVouchersByBankDateRange}`;
    const url = `${this.API_URL}${endpoint}`;

    return this._httpClient
      .post<ApiResponse<RepeatedVoucherByBankDateRange[]>>(url, {
        ...dateRange,
      })
      .pipe(
        map((response) => ({
          ...response,
          data: response.data.map((item) => ({
            ...item,
            monto:
              typeof item.monto === 'string'
                ? parseFloat(item.monto)
                : item.monto,
            total:
              typeof item.total === 'string'
                ? parseInt(item.total, 10)
                : item.total,
          })),
        }))
      );
  }

  public getAgentTransactionsSummary(
    filters: PaginationParams &
      DateRange & {
        [key: string]: string | number | boolean | Date | null | undefined;
      }
  ): Observable<ApiResponse<{ data: ProfitPerAgent[]; totalCount: number }>> {
    const endpoint = `${SubscriptionPaymentEndpointEnum.GetAgentTransactionsSummary}`;
    const url = `${this.API_URL}${endpoint}`;
    return this._httpClient.post<
      ApiResponse<{ data: ProfitPerAgent[]; totalCount: number }>
    >(url, {
      ...filters,
    });
  }

  public getExpensesPerAccount(
    dateRange: DateRange
  ): Observable<ApiResponse<ExpensesPerAccount[]>> {
    const endpoint = `${SubscriptionPaymentEndpointEnum.GetExpensesByAccount}`;
    const url = `${this.API_URL}${endpoint}`;
    return this._httpClient.post<ApiResponse<ExpensesPerAccount[]>>(url, {
      ...dateRange,
    });
  }

  public getLatestExpenseMovements(
    filters: {
      [key: string]: string | number | boolean | null | undefined;
    } = this.defaultLatestExpenseFilters
  ): Observable<
    ApiResponse<{ data: LatestExpenseMovements[]; totalCount: number }>
  > {
    const endpoint = `${SubscriptionPaymentEndpointEnum.GetLatestExpenseMovements}`;
    const url = `${this.API_URL}${endpoint}`;
    return this._httpClient
      .post<
        ApiResponse<{ data: LatestExpenseMovements[]; totalCount: number }>
      >(url, {
        ...filters,
      })
      .pipe(map(this.mapDates));
  }

  private mapDates(
    res: ApiResponse<{ data: LatestExpenseMovements[]; totalCount: number }>
  ): ApiResponse<{ data: LatestExpenseMovements[]; totalCount: number }> {
    const { data } = res;

    return {
      ...res,
      data: {
        totalCount: data.totalCount,
        data: data.data.map((item) => ({
          ...item,
          fecha: moment.utc(item.fecha).format('DD-MM-YYYY HH:mm:ss'),
        })),
      },
    };
  }

  getAllSp(
    page: number,
    limit: number,
    filters: SubscriptionPaymentQueryFilters = {}
  ): Observable<ApiResponse<SubscriptionPaymentListResponseData>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this._httpClient.get<ApiResponse<SubscriptionPaymentListResponseData>>(
      `${this.API_URL}${SubscriptionPaymentEndpointEnum.GetAllSp}`,
      { params }
    );
  }

  /**
   * Asks the API to build the report for every record matching `filters`.
   * The file is generated server-side, so the download is not limited to the
   * page currently rendered in the table.
   */
  exportAllSp(
    filters: SubscriptionPaymentQueryFilters = {},
    format: 'xlsx' | 'csv' = 'xlsx'
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams()
      .set('page', '1')
      .set('exportFile', format)
      .set('isExportAll', 'all');
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this._httpClient.get(
      `${this.API_URL}${SubscriptionPaymentEndpointEnum.GetAllSp}`,
      { params, observe: 'response', responseType: 'blob' }
    );
  }

  getSp(spId: number): Observable<ApiResponse<SubscriptionPaymentListItem>> {
    return this._httpClient.get<ApiResponse<SubscriptionPaymentListItem>>(
      `${this.API_URL}${SubscriptionPaymentEndpointEnum.GetOneSp}/${spId}`
    );
  }

  getUserEditSp(
    page: number,
    limit: number,
    searchTerm?: string
  ): Observable<ApiResponse<SubscriptionPaymentListItem[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this._httpClient.get<ApiResponse<SubscriptionPaymentListItem[]>>(
      `${this.API_URL}${SubscriptionPaymentEndpointEnum.GetUserEditSp}`,
      { params }
    );
  }

  downloadBalanceBonusData(): Observable<ApiResponse<SubscriptionPaymentListItem[]>> {
    return this._httpClient.get<ApiResponse<SubscriptionPaymentListItem[]>>(
      `${this.API_URL}${SubscriptionPaymentEndpointEnum.DownloadSpData}`
    );
  }
  setState(
    idabonos_pagos: number,
    id_estado: number,
    usuario_updated_id: number
  ): Observable<ApiResponse<null>> {
    const body = {
      idabonos_pagos: idabonos_pagos,
      id_estado: id_estado,
      usuario_updated_id: usuario_updated_id,
    };
    return this._httpClient.patch<ApiResponse<null>>(
      `${this.API_URL}${SubscriptionPaymentEndpointEnum.SetState}`,
      body
    );
  }

  updateItem(object: SubscriptionPaymentUpdatePayload): Observable<ApiResponse<null>> {
    return this._httpClient.patch<ApiResponse<null>>(
      `${this.API_URL}${SubscriptionPaymentEndpointEnum.updateItem}`,
      object
    );
  }
}
